import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  getVendorUser,
  getVendorRefreshToken,
  clearVendorSession,
} from '@/lib/vendorAuth';
import { vendorAuthApi } from '@/services/vendor.api';
import { VendorRole } from '@pms/shared';

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `block rounded-md px-3 py-2 text-sm font-medium transition ${
    isActive
      ? 'bg-slate-800 text-white'
      : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
  }`;

/**
 * Vendor console shell (TASK-144). Distinct dark chrome + a persistent "Vendor
 * Observability" realm badge, the always-on masking + audit disclosure notice,
 * and nav. Team management shows only to a vendor ADMIN.
 */
export function VendorLayout() {
  const navigate = useNavigate();
  const user = getVendorUser();

  const logout = async () => {
    const rt = getVendorRefreshToken();
    try {
      if (rt) await vendorAuthApi.logout(rt);
    } catch {
      /* best-effort */
    } finally {
      clearVendorSession();
      navigate('/vendor/login', { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      <aside className="flex w-60 flex-col border-r border-slate-800 bg-slate-900 p-4">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-400">
          Vendor Observability
        </div>
        <div className="mb-6 text-[11px] text-slate-500">innow8apps · observe-only</div>

        <nav className="flex-1 space-y-1">
          <NavLink to="/vendor" end className={navLinkClass}>
            Overview
          </NavLink>
          <NavLink to="/vendor/activity" className={navLinkClass}>
            Activity
          </NavLink>
          <NavLink to="/vendor/emails" className={navLinkClass}>
            Email
          </NavLink>
          {user?.role === VendorRole.ADMIN && (
            <NavLink to="/vendor/team" className={navLinkClass}>
              Team
            </NavLink>
          )}
        </nav>

        <div className="mt-4 space-y-2 border-t border-slate-800 pt-4">
          <div className="truncate text-xs text-slate-400" title={user?.email}>
            {user?.email}
          </div>
          <button
            onClick={logout}
            className="w-full rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1">
        <div className="border-b border-amber-500/20 bg-amber-500/10 px-6 py-2 text-xs text-amber-300">
          🔒 Read-only. Guest PII and revenue are masked. Every access here is
          audited and disclosed to BestAcco.
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
